import React from 'react';
import { useAimScorecard } from './lib/aimQueries';
import ProviderColorDot from './primitives/ProviderColorDot';
import RatingColorBar from './primitives/RatingColorBar';
import TrendArrow from './primitives/TrendArrow';
import AimEmpty, { AimLoading } from './primitives/AimEmpty';
import { prettifyProviderLabel } from './lib/providerColors';
import { NO_DATA_MESSAGE, isCollectionFailed } from './lib/dataStatus';
import { TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW, TABLE_ROW_CLICKABLE } from '../shared/tableStyles';

function pct(n) { return n == null ? '—' : `${Number(n).toFixed(1)}%`; }
function num(n) { return n == null ? '—' : Number(n).toLocaleString(); }

export default function ProviderScorecard({ clientId, runId, compareRunId, onSelectProvider }) {
  const useCompare = Boolean(compareRunId && compareRunId !== runId);
  const { data: rows, loading } = useAimScorecard(clientId, runId, useCompare ? compareRunId : null);

  if (loading) {
    return <AimLoading height={140} message="Loading provider scorecard…" />;
  }
  if (!Array.isArray(rows) || rows.length === 0) {
    return <AimEmpty message="No provider scorecard data for this run." />;
  }

  // Rank by mention rate, highest first; providers with an unmeasured mention
  // rate (null) sort to the bottom. Copy first so the hook's data isn't mutated.
  const sortedRows = [...rows].sort((a, b) => {
    const av = a.mention_pct == null ? -Infinity : Number(a.mention_pct);
    const bv = b.mention_pct == null ? -Infinity : Number(b.mention_pct);
    return bv - av;
  });

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Provider Scorecard</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">{rows.length} providers</span>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Per-provider summary for the selected run — mention rate, factual accuracy, hallucination count, and sentiment. Click a row to drill into that provider&apos;s details.
          {' '}A dash <span className="font-mono text-gray-700 dark:text-gray-300">&quot;—&quot;</span> means the metric is <em>unmeasured</em> for that provider, not zero — typically search-result providers (Google Organic, AI Overviews, AI Mode) which return rankings and summaries rather than checkable facts, so Accuracy and Hallucinations don&apos;t apply.
          {' '}<strong>No data</strong> means collection failed for that provider in this run.
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
        <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px] responsive-card-table">
          <thead className={TABLE_THEAD}>
            <tr>
              <th className="text-left px-4 py-2.5 whitespace-nowrap">Provider</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Mention</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Accuracy</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Hallucinations</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Sentiment</th>
              {useCompare && <th className="text-right px-4 py-2.5 whitespace-nowrap">Wk/Wk</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const name = r.display_name || r.provider || '—';
              // A failed collection is unmeasured, not zero: no rate bar, no 0.0%, and dashes
              // for every other metric — whatever the row happens to carry in them.
              const failed = isCollectionFailed(r);
              // Preserve null on mention/accuracy so SerpAPI providers (which
              // legitimately have no scored facts) render "—" via pct() instead
              // of being coerced to 0.0%. The empty bar + "—" is the right
              // visual signal for "unmeasured" per spec.
              const mention = !failed && r.mention_pct != null ? Number(r.mention_pct) : null;
              const accuracy = !failed && r.accuracy_pct != null ? Number(r.accuracy_pct) : null;
              const hallucinations = !failed && r.hallucinations != null ? Number(r.hallucinations) : null;
              const sentiment = !failed && r.sentiment != null ? Number(r.sentiment) : null;
              const prev = !failed && r.prev_mention_pct != null ? Number(r.prev_mention_pct) : undefined;
              return (
                <tr
                  key={`${name}-${r.provider || ''}-${r.search_enabled ? 'on' : 'off'}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open details for ${name}`}
                  className={`${TABLE_ROW} ${TABLE_ROW_CLICKABLE}`}
                  onClick={() => onSelectProvider?.(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectProvider?.(r);
                    }
                  }}
                >
                  <td className="px-4 py-3" data-label="Provider">
                    <span className="inline-flex items-center gap-2">
                      <ProviderColorDot displayName={name} />
                      <span className="font-medium text-gray-900 dark:text-white">{prettifyProviderLabel(name)}</span>
                    </span>
                    {failed && (
                      <p className="mt-0.5 pl-[18px] text-[11px] font-normal text-gray-500 dark:text-gray-400">{NO_DATA_MESSAGE}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" data-label="Mention">
                    {failed ? (
                      <span className="text-gray-500 dark:text-gray-400">No data</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24"><RatingColorBar value={mention} /></div>
                        <span className="tabular-nums">{pct(mention)}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Accuracy">{pct(accuracy)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Hallucinations">{num(hallucinations)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Sentiment">{sentiment != null ? sentiment.toFixed(0) : '—'}</td>
                  {useCompare && (
                    <td className="px-4 py-3 text-right" data-label="Wk/Wk">
                      <TrendArrow current={mention} previous={prev} suffix="%" />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
